<template>
  <div>
    <v-container
      class="fluid-max-width pa-0 print-excluded"
      v-if="summary && summary.length > 0 && user && site"
    >
      <div class="pa-4">
        <p class="text-h4 ma-0">Schachtkonfigurator</p>
        <p class="text-h4 title primary--text ma-0">Bestellung</p>
        <p class="mt-4">
          {{ formatDate(summary[0].order_date) }},
          {{ formatTime(summary[0].order_date) }} Uhr
        </p>
        <v-row>
          <v-col cols="12" md="6">
            <h5 class="mb-2 text-uppercase">Kunde</h5>
            <p>
              <span>
                {{ user.first_name }}
                {{ user.last_name }}
              </span>
              <br />
              <span>{{ user.email }}</span>
              <template v-if="user.telephone">
                <br />
                <span>
                  {{ user.telephone }}
                </span>
              </template>
            </p>
            <p>
              <span>
                {{ company.name }}
              </span>
              <br />
              <span>
                {{ company.street }}
              </span>
              <br />
              <span> {{ company.postal_code }} {{ company.city }} </span>
            </p>
            <p>Kd.Nr: {{ company.customer_no }}</p>
          </v-col>
          <v-col cols="12" md="6">
            <h5 class="mb-2 text-uppercase">Baustelle</h5>
            <p>
              <span>
                {{ site.name }}
              </span>
              <template v-if="site.street">
                <br />
                <span>{{ site.street }}</span>
              </template>
              <br />
              <span>{{ site.postal_code }} {{ site.city }} </span>
            </p>
            <p v-if="site.cost_center">Kostenstelle: {{ site.cost_center }}</p>
            <template v-if="summary[0].delivery_date">
              <h5 class="mb-2 text-uppercase">Gewünschter Liefertermin</h5>
              <p>{{ formatDate(summary[0].delivery_date) }}</p>
            </template>
            <h5
              class="mb-2 text-uppercase"
              v-if="
                site.contact_person_last_name && site.contact_person2_last_name
              "
            >
              Ansprechpersonen
            </h5>
            <h5
              class="mb-2 text-uppercase"
              v-else-if="
                site.contact_person_last_name || site.contact_person2_last_name
              "
            >
              Ansprechperson
            </h5>
            <template v-if="site.contact_person_last_name">
              <p class="ma-0">
                <span>
                  {{ site.contact_person_first_name }}
                  {{ site.contact_person_last_name }}
                </span>
                <span v-if="site.contact_person_position">
                  ({{ site.contact_person_position }})</span
                >
                <br />
                <span>{{ site.contact_person_telephone }}</span>
              </p>
            </template>
            <template v-if="site.contact_person2_last_name">
              <p class="ma-0">
                <span>
                  {{ site.contact_person2_first_name }}
                  {{ site.contact_person2_last_name }}
                </span>
                <span v-if="site.contact_person2_position">
                  ({{ site.contact_person2_position }})</span
                >
                <br />
                <span>{{ site.contact_person2_telephone }}</span>
              </p>
            </template>
          </v-col>
        </v-row>
      </div>
      <v-expansion-panels
        flat
        tile
        multiple
        v-model="expanded"
        class="shafts-list"
      >
        <v-expansion-panel v-for="item in summary" :key="item.id" class="mb-1">
          <v-expansion-panel-header class="grey lighten-3">
            <p class="pa-0 ma-0 mb-1">
              <span class="v-list-item__title font-weight-bold">{{
                item.title
              }}</span>
            </p>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-container class="ma-0 pa-0">
              <v-row class="mt-6 pt-6">
                <v-col
                  cols="12"
                  lg="4"
                  md="6"
                  class="d-flex flex-column align-center"
                >
                  <ShaftImage
                    class="summary-shaft-image summary-shaft-base-image"
                    :shaft="item"
                    showLabels="true"
                    showDegrees="true"
                  />
                </v-col>
                <v-col cols="12" lg="8" md="6">
                  <div class="print-list">
                    <h5 class="mb-2 text-uppercase">
                      Schacht Basisinformationen
                    </h5>
                    <OrderSummaryListItem
                      label="Typ"
                      :value="item.type.title"
                    />
                    <OrderSummaryListItem
                      label="Material/Farbe"
                      :value="item.subtype.type"
                    />
                    <OrderSummaryListItem
                      label="Versetzanker"
                      :value="item.anchor ? 'Ja' : 'Nein'"
                    />

                    <h5 class="mt-6 mb-2 text-uppercase">
                      Hauptgerinne Einlauf
                    </h5>
                    <OrderSummaryListItem
                      label="Material"
                      :value="
                        item.input_channel.material
                          ? item.input_channel.material.material
                          : ''
                      "
                      v-if="
                        item.input_channel.diameter &&
                          item.input_channel.diameter.dn > 0
                      "
                    />
                    <OrderSummaryListItem
                      label="Fabrikat"
                      :value="item.input_channel.note"
                      v-if="item.input_channel.note"
                    />
                    <OrderSummaryListItem
                      label="Durchmesser"
                      :value="item.input_channel.diameter.dn_label"
                    />
                    <OrderSummaryListItem
                      label="Muffenschrägstellung"
                      :value="item.input_channel.tilt + '‰'"
                      v-if="
                        item.input_channel.diameter &&
                          item.input_channel.diameter.dn > 0 &&
                          item.input_channel.tilt > 0
                      "
                    />
                    <OrderSummaryListItem
                      label="Höher/Tiefer"
                      :value="item.input_channel.offset + ' cm'"
                      v-if="
                        item.input_channel.diameter &&
                          item.input_channel.diameter.dn > 0 &&
                          item.input_channel.offset > 0
                      "
                    />
                    <OrderSummaryListItem
                      label="Gefälle Hauptgerinne"
                      :value="item.slope + '°'"
                      v-if="
                        item.input_channel.diameter &&
                          item.input_channel.diameter.dn > 0 &&
                          item.slope > 0
                      "
                    />

                    <h5 class="mt-6 mb-2 text-uppercase">
                      Hauptgerinne Auslauf
                    </h5>
                    <OrderSummaryListItem
                      label="Material"
                      :value="
                        item.output_channel.material
                          ? item.output_channel.material.material
                          : ''
                      "
                      v-if="
                        item.output_channel.diameter &&
                          item.output_channel.diameter.dn > 0
                      "
                    />
                    <OrderSummaryListItem
                      label="Fabrikat"
                      :value="item.output_channel.note"
                      v-if="
                        item.output_channel.diameter &&
                          item.output_channel.diameter.dn > 0 &&
                          item.output_channel.note
                      "
                    />
                    <OrderSummaryListItem
                      label="Durchmesser"
                      :value="item.output_channel.diameter.dn_label"
                    />
                    <OrderSummaryListItem
                      label="Richtungsänderung"
                      :value="item.output_channel.angle + '°'"
                      v-if="
                        item.output_channel.diameter &&
                          item.output_channel.diameter.dn > 0
                      "
                    />
                    <OrderSummaryListItem
                      label="Muffenschrägstellung"
                      :value="item.output_channel.tilt + '‰'"
                      v-if="
                        item.output_channel.diameter &&
                          item.output_channel.diameter.dn > 0 &&
                          item.output_channel.tilt > 0
                      "
                    />
                    <OrderSummaryListItem
                      label="Höher/Tiefer"
                      :value="item.output_channel.offset + ' cm'"
                      v-if="
                        item.output_channel.diameter &&
                          item.output_channel.diameter.dn > 0 &&
                          item.output_channel.offset > 0
                      "
                    />

                    <div
                      v-for="(sideChannel, index) in item.side_channels"
                      :key="sideChannel.id"
                    >
                      <h5 class="mt-6 mb-2 text-uppercase">
                        Seitenzulauf {{ index + 1 }}
                      </h5>
                      <OrderSummaryListItem
                        label="Material"
                        :value="
                          sideChannel.material
                            ? sideChannel.material.material
                            : ''
                        "
                      />
                      <OrderSummaryListItem
                        label="Fabrikat"
                        :value="sideChannel.note"
                        v-if="sideChannel.note"
                      />
                      <OrderSummaryListItem
                        label="Durchmesser"
                        :value="'DN' + sideChannel.diameter.dn"
                      />
                      <OrderSummaryListItem
                        label="Richtungsänderung"
                        :value="sideChannel.angle + '°'"
                      />
                      <OrderSummaryListItem
                        label="Muffenschrägstellung"
                        :value="sideChannel.tilt + '‰'"
                        v-if="sideChannel.tilt > 0"
                      />
                      <OrderSummaryListItem
                        label="Scheitel"
                        :value="sideChannel.alignment"
                        v-if="sideChannel.alignment"
                      />
                      <OrderSummaryListItem
                        label="Höher/Tiefer"
                        :value="sideChannel.offset + ' cm'"
                        v-if="sideChannel.alignment && sideChannel.offset && sideChannel.offset !== 0"
                      />
                    </div>
                  </div>
                </v-col>
              </v-row>
              <v-row class="mt-6 pt-6" v-if="item.cover">
                <v-col
                  cols="12"
                  lg="4"
                  md="6"
                  class="d-flex flex-column align-center"
                >
                  <ShaftImageBuildup
                    class="summary-shaft-image summary-shaft-buildup-image"
                    :shaft="item"
                    showLabels="true"
                  />
                </v-col>
                <v-col cols="12" lg="8" md="6">
                  <div class="print-list">
                    <h5 class="mb-2 text-uppercase">
                      Schachtaufbau Basisinformationen
                    </h5>
                    <OrderSummaryListItem
                      label="Typ"
                      :value="
                        `${item.cover.type} (Art.-Nr. ${item.cover.article_number})`
                      "
                    />
                    <OrderSummaryListItem
                      label="Geländeoberkante"
                      :value="('' + item.top_edge).replace('.', ',') + ' m'"
                    />
                    <OrderSummaryListItem
                      label="Gerinne Sohle"
                      :value="('' + item.bottom_edge).replace('.', ',') + ' m'"
                    />
                    <OrderSummaryListItem
                      label="Steigbügel"
                      :value="item.clamp"
                    />
                    <OrderSummaryListItem
                      label="Dichtung"
                      :value="item.sealing"
                    />

                    <h5 class="mt-6 mb-2 text-uppercase">
                      Schachtaufbau
                    </h5>
                    <OrderSummaryListItem
                      label="Konus"
                      :value="
                        `${item.cone.height} cm ${item.cone.title} (Art.-Nr. ${item.cone.article_number})`
                      "
                      v-if="item.cone"
                    />
                    <OrderSummaryListItem
                      :label="'Schachtring ' + (index + 1)"
                      :value="
                        `${buildup.type.height} cm ${buildup.type.title} (Art.-Nr. ${buildup.type.article_number})`
                      "
                      v-for="(buildup, index) in buildups(item).filter(
                        b => b.type,
                      )"
                      :key="buildup.id"
                    />
                  </div>
                </v-col>
              </v-row>
            </v-container>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-container>
    <v-container v-else>
      <h4 class="text-center mt-4">Schachtbestellung nicht vorhanden.</h4>
    </v-container>
  </div>
</template>

<script>
import { formatTime, formatDate } from '@/utils/utils';
import ShaftImage from '@/components/ShaftImage.vue';
import ShaftImageBuildup from '@/components/ShaftImageBuildup.vue';
import OrderSummaryListItem from '@/components/OrderSummaryListItem.vue';

export default {
  name: 'OrderSummaryResponsiveLayout',
  components: { ShaftImage, ShaftImageBuildup, OrderSummaryListItem },
  props: ['summary'],
  data: () => ({
    expanded: [],
  }),
  computed: {
    user() {
      if (!this.summary || this.summary.length === 0) return undefined;
      return this.summary[0].order_user;
    },
    company() {
      if (!this.summary || this.summary.length === 0) return undefined;
      return this.summary[0].site_id.company;
    },
    site() {
      if (!this.summary || this.summary.length === 0) return undefined;
      return this.summary[0].site_id;
    },
  },
  watch: {
    summary() {
      if (this.summary) {
        this.expanded = [];
        this.summary.forEach((_, index) => {
          this.expanded.push(index);
        });
      }
    },
  },
  methods: {
    formatDate,
    formatTime,
    buildups(item) {
      if (item.buildups) {
        const copy = [...item.buildups];
        return copy.reverse();
      }
      return [];
    },
  },
};
</script>

<style scoped>
.title {
  font-family: 'Playfair Display', sans-serif !important;
}

.summary-shaft-base-image {
  max-width: 350px;
}

.summary-shaft-buildup-image {
  max-width: 250px;
}
</style>
